import classNames from 'classnames';
import { FunctionComponent } from 'react';

import { useBundle } from '@amzn/react-arb-tools';

import { ButtonLink, ButtonLinkVariant } from 'src/components/common/menu-item/button-link/ButtonLink';
import { CloseButton } from 'src/components/common/menu-item/close-button/CloseButton';
import { CopyRight } from 'src/components/common/menu-item/copyright/CopyRight';
import { HamburgerButton } from 'src/components/common/menu-item/hamburger-button/HamburgerButton';
import { Link, LinkStyleVariant } from 'src/components/common/menu-item/link/Link';
import { Logo } from 'src/components/common/menu-item/logo/Logo';
import { Parent, ParentStyleVariant } from 'src/components/common/menu-item/parent/Parent';
import { SearchButtonLink } from 'src/components/common/menu-item/search-button-link/SearchButtonLink';
import { SubNavTitle } from 'src/components/common/menu-item/subnav-title/SubNavTitle';
import { NAV_ITEM_TYPE, NAV_PLACEMENT } from 'src/enums/navigation';
import { RESOURCE_BUNDLES } from 'src/enums/resource-bundles';
import { THEME } from 'src/enums/theme';
import { tf } from 'src/helpers';
import { NavItem } from 'src/interfaces/navigation';

import styles from './MenuItem.module.scss';
import { Account } from './account/Account';
import { HeadingLink } from './heading-link/HeadingLink';
import { LocalePicker } from './locale-picker/LocalePicker';

interface MenuItemProps {
  isAlignedRight?: boolean;
  isAuthenticated?: boolean;
  isDeviceMenuExpanded?: boolean;
  isItemActive?: boolean;
  isItemExpanded?: boolean;
  isNavExpanded?: boolean;
  theme?: THEME;
  navItem: NavItem;
  onDeviceMenuExpand?: () => void;
  onDeviceMenuCollapse?: () => void;
  onItemActivate?: (navItem: NavItem) => void;
  onMenuExpand?: (navItem: NavItem) => void;
  onMenuCollapse?: () => void;
  placement: NAV_PLACEMENT;
  resourceBundle: RESOURCE_BUNDLES;
}

const getLinkStyleVariant = (
  isFooter: boolean,
  isSubNav: boolean,
  isNavExpanded: boolean,
  isDeviceNav: boolean,
  isDeviceSubNav: boolean
): LinkStyleVariant => {
  if (isFooter) {
    return 'secondary';
  }

  if (isSubNav) {
    return 'tertiary';
  }

  if (isDeviceNav || isDeviceSubNav) {
    return 'quaternary';
  }

  if (isNavExpanded) {
    return 'secondary';
  }

  return 'primary';
};

const getButtonLinkStyleVariant = (isFooter: boolean, isNavExpanded: boolean): ButtonLinkVariant => {
  if (isFooter) {
    return 'tertiary';
  }

  return isNavExpanded ? 'secondary' : 'primary';
};

const getParentStyleVariant = (
  isNavExpanded: boolean,
  isDeviceNav: boolean,
  isDeviceSubNav: boolean
): ParentStyleVariant => {
  if (isDeviceNav) {
    return 'tertiary';
  }

  if (isDeviceSubNav) {
    return 'quaternary';
  }

  return isNavExpanded ? 'secondary' : 'primary';
};

// TODO: Clean up hardcoded values for launch and use the NRS provided ones instead
// https://taskei.amazon.dev/tasks/ASX-367
const XCM_BRANDING_LOGO_TEXT = 'Sustainability Exchange';
const XCM_BRANDING_LOGO_URL = '/';

// This component's job is to select which menu item it should display and wrap it in an appropriately styled <li />
export const MenuItem: FunctionComponent<MenuItemProps> = ({
  isAlignedRight = false,
  isAuthenticated,
  isDeviceMenuExpanded = false,
  isItemActive = false,
  isItemExpanded = false,
  isNavExpanded = false,
  theme = THEME.LIGHT,
  navItem,
  onDeviceMenuCollapse,
  onDeviceMenuExpand,
  onItemActivate,
  onMenuExpand,
  onMenuCollapse,
  placement,
  resourceBundle,
}) => {
  const { itemType, label, url, stringId } = navItem;
  const isHeader = placement === NAV_PLACEMENT.HEADER;
  const isFooter = placement === NAV_PLACEMENT.FOOTER;
  const isSubNav = placement === NAV_PLACEMENT.SUB_NAV;
  const isDeviceNav = placement === NAV_PLACEMENT.DEVICE_NAV;
  const isDeviceSubNav = placement === NAV_PLACEMENT.DEVICE_SUB_NAV;
  const linkStyleVariant = getLinkStyleVariant(isFooter, isSubNav, isNavExpanded, isDeviceNav, isDeviceSubNav);
  const isBrandingWeblabOn = true;
  const [bundle] = useBundle(resourceBundle);

  const handleParentClick = () => {
    if (isNavExpanded || isItemExpanded) {
      onMenuCollapse?.();
    } else {
      onMenuExpand?.(navItem);
    }
  };

  const handleMouseEnter = () => {
    onItemActivate?.(navItem);
  };

  const handleHamburgerClick = () => {
    onDeviceMenuExpand?.();
  };

  const handleCloseClick = () => {
    onDeviceMenuCollapse?.();
  };

  const isLogo = itemType === NAV_ITEM_TYPE.LOGO;
  const isCopyRight = itemType === NAV_ITEM_TYPE.COPYRIGHT;
  const isCloseButton = itemType === NAV_ITEM_TYPE.CLOSE;
  const isHamburgerButton = itemType === NAV_ITEM_TYPE.HAMBURGER;
  const isSearch = itemType === NAV_ITEM_TYPE.SEARCH;
  const isSustainabilitySiteLink = itemType === NAV_ITEM_TYPE.SUST_SITE_LINK;
  const isSubNavTitle = itemType === NAV_ITEM_TYPE.SUB_NAV_TITLE;
  const isLocalePicker = itemType === NAV_ITEM_TYPE.LOCALE_PICKER;
  const isAccount = itemType === NAV_ITEM_TYPE.ACCOUNT;
  const isDeviceSuppressedInHeader =
    isHeader && !(isLogo || isSearch || isHamburgerButton || isCloseButton || isSustainabilitySiteLink);

  return (
    <li
      className={classNames(
        styles.listItem,
        { [styles.rightAlign]: isAlignedRight },
        { [styles.copyRight]: isCopyRight },
        { [styles.hamburgerButton]: isHamburgerButton },
        { [styles.localePicker]: isLocalePicker },
        { [styles.account]: isAccount },
        { [styles.closeButton]: isCloseButton },
        { [styles.showCloseButton]: isCloseButton && isDeviceMenuExpanded },
        { [styles.deviceSuppressed]: isDeviceSuppressedInHeader },
        { [styles.footer]: isFooter },
        { [styles.sustSiteLinkInHeader]: isHeader && isSustainabilitySiteLink },
        { [styles.subNavTitle]: isSubNav && isSubNavTitle }
      )}
      onMouseEnter={handleMouseEnter}
    >
      {itemType === NAV_ITEM_TYPE.LINK && (
        <Link href={url} styleVariant={linkStyleVariant} text={tf(bundle, stringId, label)} theme={theme} />
      )}

      {itemType === NAV_ITEM_TYPE.PARENT && (
        <Parent
          isExpanded={isItemExpanded}
          onClick={handleParentClick}
          styleVariant={getParentStyleVariant(isNavExpanded, isDeviceNav, isDeviceSubNav)}
          text={tf(bundle, stringId, label)}
          theme={theme}
        />
      )}

      {isLogo && (
        <Logo
          href={url}
          altText={tf(bundle, stringId, label)}
          styleVariant={isNavExpanded || isDeviceMenuExpanded ? 'secondary' : 'primary'}
          theme={theme}
        />
      )}

      {isSustainabilitySiteLink && !isFooter && isBrandingWeblabOn && (
        <HeadingLink
          href={XCM_BRANDING_LOGO_URL}
          text={XCM_BRANDING_LOGO_TEXT}
          variant={isNavExpanded || isDeviceMenuExpanded ? 'secondary' : 'primary'}
          theme={theme}
        />
      )}

      {isSustainabilitySiteLink && isFooter && (
        <ButtonLink
          href={url}
          text={tf(bundle, stringId, label)}
          variant={getButtonLinkStyleVariant(isFooter, isNavExpanded || isDeviceMenuExpanded)}
          theme={theme}
        />
      )}

      {isSustainabilitySiteLink && !isBrandingWeblabOn && !isFooter && (
        <ButtonLink
          href={url}
          text={tf(bundle, stringId, label)}
          variant={getButtonLinkStyleVariant(isFooter, isNavExpanded || isDeviceMenuExpanded)}
          theme={theme}
        />
      )}

      {isCopyRight && <CopyRight text={tf(bundle, stringId, label)} />}

      {itemType === NAV_ITEM_TYPE.EXTERNAL_LINK && (
        <Link
          href={url}
          styleVariant={isFooter || isNavExpanded ? 'secondary' : 'primary'}
          text={tf(bundle, stringId, label)}
          variant="external"
        />
      )}

      {isSearch && !isDeviceMenuExpanded && (
        <SearchButtonLink
          href={url}
          styleVariant={isNavExpanded ? 'secondary' : 'primary'}
          text={tf(bundle, stringId, label)}
          theme={theme}
        />
      )}

      {isItemActive && (
        <div
          className={classNames(styles.activeIndicator, styles[theme], {
            [styles.secondary]: isNavExpanded,
            [styles.logo]: isLogo,
          })}
          data-testid="sss-active-indicator"
        />
      )}

      {isHamburgerButton && !isDeviceMenuExpanded && (
        <HamburgerButton ariaLabel={label} onClick={handleHamburgerClick} theme={theme} />
      )}

      {isLocalePicker && (
        <LocalePicker variant={isNavExpanded || isDeviceMenuExpanded ? 'secondary' : 'primary'} theme={theme} />
      )}

      {isAccount && (
        <Account
          variant={isNavExpanded || isDeviceMenuExpanded ? 'secondary' : 'primary'}
          theme={theme}
          isAuthenticated={isAuthenticated}
        />
      )}

      {isCloseButton && isDeviceMenuExpanded && (
        <CloseButton ariaLabel={label} onClick={handleCloseClick} theme={theme} />
      )}

      {isSubNavTitle && (isSubNav || isDeviceSubNav) && (
        <SubNavTitle text={tf(bundle, stringId, label)} theme={theme} />
      )}
    </li>
  );
};
